import {
  API_BASE_URL,
  fetchBackendConfiguration,
  getAuthHeaders,
} from '../../../../utils/utils';

const CACHE_KEY = (email) => `gre_flashcards_${email}`;
const HISTORY_CACHE_KEY = (email) => `gre_flashcard_review_history_${email}`;
const PENDING_KEY = (email) => `gre_flashcards_pending_${email}`;

const buildFlashcardRequestError = (status, fallbackMessage) => {
  if (status === 401) {
    return new Error('Authentication failed');
  }

  return new Error(fallbackMessage || `HTTP ${status}`);
};

export async function lookupGreWords(words = []) {
  const uniqueWords = [
    ...new Set(
      (words || [])
        .map((word) => String(word || '').trim())
        .filter(Boolean)
    ),
  ];
  if (uniqueWords.length === 0) {
    return [];
  }

  const merged = [];
  try {
    for (let offset = 0; offset < uniqueWords.length; offset += 200) {
      const chunk = uniqueWords.slice(offset, offset + 200);
      const res = await fetch(`${API_BASE_URL}/api/gre/words/lookup`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ words: chunk }),
      });
      if (!res.ok) {
        throw buildFlashcardRequestError(res.status, 'Word lookup failed');
      }
      const data = await res.json();
      if (Array.isArray(data.words)) {
        merged.push(...data.words);
      }
    }
    return merged;
  } catch (err) {
    if (err?.message === 'Authentication failed') {
      throw err;
    }
    console.warn('lookupGreWords failed:', err.message);
    return merged;
  }
}

function _readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // quota exceeded or similar — non-critical
  }
}

function _queuePendingAction(email, action) {
  try {
    const key = PENDING_KEY(email);
    let existing = _readCache(key) || [];
    if (action.type === 'delete') {
      existing = existing.filter(
        (a) => !(a.type === 'delete' && a.flashcardId === action.flashcardId)
      );
    }
    if (action.type === 'update') {
      const sameId = existing.filter(
        (a) => a.type === 'update' && a.flashcardId === action.flashcardId
      );
      existing = existing.filter(
        (a) => !(a.type === 'update' && a.flashcardId === action.flashcardId)
      );
      const merged = sameId.reduce(
        (acc, a) => ({ ...acc, ...(a.updates || {}) }),
        {}
      );
      existing.push({
        ...action,
        updates: { ...merged, ...action.updates },
        timestamp: Date.now(),
      });
      _writeCache(key, existing);
      return;
    }
    existing.push({ ...action, timestamp: Date.now() });
    _writeCache(key, existing);
  } catch {
    // non-critical
  }
}

function _purgeFlashcardFromLocalCache(email, flashcardId) {
  const cached = _readCache(CACHE_KEY(email)) || [];
  _writeCache(CACHE_KEY(email), cached.filter((c) => c.id !== flashcardId));
}

/** Keeps first pending delete per flashcardId; drops redundant copies from failed sync replays. */
function _dedupeRedundantPendingDeletes(actions) {
  const seenDeleteIds = new Set();
  const out = [];
  for (const a of actions) {
    if (a.type === 'delete') {
      if (seenDeleteIds.has(a.flashcardId)) continue;
      seenDeleteIds.add(a.flashcardId);
    }
    out.push(a);
  }
  return out;
}

/** Merges consecutive pending updates for the same flashcard into one action (last field wins). */
function _mergeAdjacentPendingUpdates(actions) {
  const out = [];
  for (const a of actions) {
    if (a.type === 'update' && out.length > 0) {
      const last = out[out.length - 1];
      if (last.type === 'update' && last.flashcardId === a.flashcardId) {
        out[out.length - 1] = {
          ...last,
          updates: { ...(last.updates || {}), ...(a.updates || {}) },
          timestamp: a.timestamp ?? last.timestamp,
        };
        continue;
      }
    }
    out.push(a);
  }
  return out;
}

function _normalizePendingActionQueue(actions) {
  return _mergeAdjacentPendingUpdates(_dedupeRedundantPendingDeletes(actions));
}

// ── Fetch all flashcards ────────────────────────────────────────────────────

export async function fetchFlashcards(email) {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards`,
      {
        headers: getAuthHeaders(),
      }
    );
    if (!res.ok) throw buildFlashcardRequestError(res.status);
    const data = await res.json();
    if (data.success) {
      _writeCache(CACHE_KEY(email), data.flashcards);
      return data.flashcards;
    }
    throw new Error(data.message || 'Failed to fetch flashcards');
  } catch (err) {
    if (err?.message === 'Authentication failed') {
      throw err;
    }
    console.warn('fetchFlashcards API failed, falling back to cache:', err.message);
    return _readCache(CACHE_KEY(email)) || [];
  }
}

// ── Add one flashcard ───────────────────────────────────────────────────────

export async function addFlashcard(email, cardData) {
  const res = await fetch(
    `${API_BASE_URL}/api/gre/flashcards`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        word: cardData.word,
        definition_en: cardData.definition_en || null,
        definition_fr: cardData.definition_fr || null,
        example_sentence_en: cardData.example_sentence_en || null,
        example_sentence_fr: cardData.example_sentence_fr || null,
        difficulty_level: cardData.difficulty_level || 'medium',
        emoticon: cardData.emoticon || null,
        personal_note: cardData.personalNote || cardData.personal_note || null,
        question: cardData.question || null,
        options: cardData.options || null,
        added_at: cardData.addedAt || cardData.added_at || new Date().toISOString(),
      }),
    }
  );
  if (!res.ok) {
    _queuePendingAction(email, { type: 'add', card: cardData });
    throw new Error(`HTTP ${res.status}`);
  }
  const data = await res.json();

  if (data.flashcard) {
    const cached = _readCache(CACHE_KEY(email)) || [];
    const existsIdx = cached.findIndex((c) => c.id === data.flashcard.id);
    if (existsIdx === -1) {
      cached.unshift(data.flashcard);
    } else {
      cached[existsIdx] = data.flashcard;
    }
    _writeCache(CACHE_KEY(email), cached);
  }

  return data;
}

// ── Update a flashcard ──────────────────────────────────────────────────────

export async function updateFlashcard(email, flashcardId, updates, options = {}) {
  const { fromSync = false } = options;
  try {
    const body = {};
    const fieldMap = {
      personalNote: 'personal_note',
      definition_en: 'definition_en',
      definition_fr: 'definition_fr',
      example_sentence_en: 'example_sentence_en',
      example_sentence_fr: 'example_sentence_fr',
      difficulty_level: 'difficulty_level',
      emoticon: 'emoticon',
      personal_note: 'personal_note',
      question: 'question',
      options: 'options',
      reviewCount: 'review_count',
      review_count: 'review_count',
      easeFactor: 'ease_factor',
      ease_factor: 'ease_factor',
      interval: 'interval',
      nextReviewDate: 'next_review_date',
      next_review_date: 'next_review_date',
      lastReviewed: 'last_reviewed',
      last_reviewed: 'last_reviewed',
      word: 'word',
    };

    for (const [key, value] of Object.entries(updates)) {
      const apiKey = fieldMap[key] || key;
      body[apiKey] = value;
    }

    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/${flashcardId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      }
    );

    if (res.status === 404) {
      _purgeFlashcardFromLocalCache(email, flashcardId);
      return { success: true };
    }

    if (!res.ok) throw buildFlashcardRequestError(res.status);

    let data;
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (data.success && data.flashcard) {
      const cached = _readCache(CACHE_KEY(email)) || [];
      const idx = cached.findIndex((c) => c.id === flashcardId);
      if (idx !== -1) {
        cached[idx] = data.flashcard;
        _writeCache(CACHE_KEY(email), cached);
      }
    }

    return data;
  } catch (err) {
    if (!fromSync) {
      console.warn('updateFlashcard API failed, queuing offline:', err.message);
      _queuePendingAction(email, { type: 'update', flashcardId, updates });
    }
    throw err;
  }
}

// ── Delete a flashcard ──────────────────────────────────────────────────────

export async function deleteFlashcard(email, flashcardId, options = {}) {
  const { fromSync = false } = options;
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/${flashcardId}`,
      { method: 'DELETE', headers: getAuthHeaders() }
    );

    if (res.status === 404) {
      _purgeFlashcardFromLocalCache(email, flashcardId);
      return { success: true };
    }

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    let data;
    try {
      data = await res.json();
    } catch {
      data = { success: true };
    }

    if (data.success) {
      _purgeFlashcardFromLocalCache(email, flashcardId);
    }

    return data;
  } catch (err) {
    if (!fromSync) {
      console.warn('deleteFlashcard API failed, queuing offline:', err.message);
      _queuePendingAction(email, { type: 'delete', flashcardId });
    }
    throw err;
  }
}

// ── Bulk add flashcards ─────────────────────────────────────────────────────

export async function bulkAddFlashcards(email, cards) {
  const payload = cards.map((c) => ({
    word: c.word,
    definition_en: c.definition_en || null,
    definition_fr: c.definition_fr || null,
    example_sentence_en: c.example_sentence_en || null,
    example_sentence_fr: c.example_sentence_fr || null,
    difficulty_level: c.difficulty_level || 'medium',
    emoticon: c.emoticon || null,
    personal_note: c.personalNote || c.personal_note || null,
    question: c.question || null,
    options: c.options || null,
    review_count: c.reviewCount || c.review_count || 0,
    ease_factor: c.easeFactor || c.ease_factor || 2.5,
    interval: c.interval || 0,
    next_review_date: c.nextReviewDate || c.next_review_date || null,
    last_reviewed: c.lastReviewed || c.last_reviewed || null,
    added_at: c.addedAt || c.added_at || new Date().toISOString(),
  }));

  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/bulk`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ flashcards: payload }),
      }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('bulkAddFlashcards API failed, queuing offline:', err.message);
    _queuePendingAction(email, { type: 'bulk_add', cards: payload });
    throw err;
  }
}

// ── Get flashcard count ─────────────────────────────────────────────────────

export async function getFlashcardCount(email) {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/count`,
      {
        headers: getAuthHeaders(),
      }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.count || 0;
  } catch (err) {
    if (err?.message === 'Authentication failed') {
      throw err;
    }
    const cached = _readCache(CACHE_KEY(email));
    return cached ? cached.length : 0;
  }
}

// ── Review history ──────────────────────────────────────────────────────────

export async function fetchReviewHistory(email) {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/review-history`,
      {
        headers: getAuthHeaders(),
      }
    );
    if (!res.ok) throw buildFlashcardRequestError(res.status);
    const data = await res.json();
    if (data.success) {
      _writeCache(HISTORY_CACHE_KEY(email), data.history);
      return data.history;
    }
    throw buildFlashcardRequestError(res.status, 'Failed');
  } catch (err) {
    if (err?.message === 'Authentication failed') {
      throw err;
    }
    return _readCache(HISTORY_CACHE_KEY(email)) || [];
  }
}

// ── Record review event ─────────────────────────────────────────────────────

export async function recordReviewEvent(email, isCorrect) {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/review-event`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_correct: isCorrect }),
      }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('recordReviewEvent failed:', err.message);
    _queuePendingAction(email, { type: 'review_event', isCorrect });
  }
}

// ── Bulk import review history (migration) ──────────────────────────────────

export async function bulkImportReviewHistory(email, history) {
  const payload = history.map((e) => ({
    date: e.date,
    cards_reviewed: e.cardsReviewed || e.cards_reviewed || 0,
    correct: e.correct || 0,
    incorrect: e.incorrect || 0,
  }));

  const res = await fetch(
    `${API_BASE_URL}/api/gre/flashcards/review-history/bulk`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}

// ── Report mastery ──────────────────────────────────────────────────────────

export async function fetchMastery(email) {
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/gre/flashcards/mastery`,
      {
        headers: getAuthHeaders(),
      }
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('fetchMastery failed:', err.message);
    return { success: false, mastered: [] };
  }
}

// ── Old UserConfiguration helpers (migration) ──────────────────────────────

export async function fetchOldConfigFlashcards(email) {
  try {
    const config = await fetchBackendConfiguration(email, {
      scope: 'legacy_flashcards',
    });
    if (!config) return { cards: [], history: [], rawConfig: null };
    let cards = [];
    let history = [];

    for (const [key, value] of Object.entries(config)) {
      if (key.startsWith('gre_flashcards_') && !key.includes('mastered') && !key.includes('review_history') && !key.includes('backup') && !key.includes('pending')) {
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value;
          if (Array.isArray(parsed) && parsed.length > cards.length) {
            cards = parsed;
          }
        } catch { /* skip */ }
      }
      if (key.startsWith('gre_flashcard_review_history_')) {
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value;
          if (Array.isArray(parsed) && parsed.length > history.length) {
            history = parsed;
          }
        } catch { /* skip */ }
      }
    }

    return { cards, history, rawConfig: config };
  } catch (err) {
    console.warn('fetchOldConfigFlashcards failed:', err.message);
    return { cards: [], history: [], rawConfig: null };
  }
}

export async function cleanOldConfigFlashcards(email) {
  try {
    const fullConfig = await fetchBackendConfiguration(email, {
      forceRefresh: true,
      scope: 'full',
    });
    if (!fullConfig) return;

    const cleaned = {};
    for (const [key, value] of Object.entries(fullConfig)) {
      if (
        key.startsWith('gre_flashcards_') ||
        key.startsWith('gre_flashcard_mastered_') ||
        key.startsWith('gre_flashcard_review_history_')
      ) {
        continue;
      }
      cleaned[key] = value;
    }
    await fetch(`${API_BASE_URL}/api/user/app/configure/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ configuration: cleaned, merge: false }),
    });
  } catch (err) {
    console.warn('cleanOldConfigFlashcards failed:', err.message);
  }
}

// ── Sync pending offline changes ────────────────────────────────────────────

export async function syncPendingChanges(email) {
  const key = PENDING_KEY(email);
  const pending = _readCache(key);
  if (!pending || pending.length === 0) return;

  const snapshot = _normalizePendingActionQueue([...pending]);
  try { localStorage.removeItem(key); } catch {}

  const failed = [];
  for (const action of snapshot) {
    try {
      switch (action.type) {
        case 'add':
          await addFlashcard(email, action.card);
          break;
        case 'update':
          await updateFlashcard(email, action.flashcardId, action.updates, {
            fromSync: true,
          });
          break;
        case 'delete':
          await deleteFlashcard(email, action.flashcardId, { fromSync: true });
          break;
        case 'review_event':
          await recordReviewEvent(email, action.isCorrect);
          break;
        case 'bulk_add':
          await bulkAddFlashcards(email, action.cards);
          break;
        default:
          break;
      }
    } catch {
      failed.push(action);
    }
  }

  if (failed.length > 0) {
    const current = _readCache(key) || [];
    _writeCache(key, _normalizePendingActionQueue([...failed, ...current]));
  }
}
